import type { User, UserId } from "@/api/users";
import type { MarkdownIt, StateBlock } from "markdown-it";

const OPEN = /^\[quote user=([A-Za-z0-9@._-]+) comment=([A-Za-z0-9-]+)\]$/;
const CLOSE = /^\[\/quote\]$/;

// Matches an innermost quote block (its body contains no further quote markers),
// so repeated removal strips arbitrarily nested quotes without leaving orphans.
const INNERMOST_QUOTE =
    /\[quote user=[^\]\n]*\](?:(?!\[quote user=)(?!\[\/quote\])[\s\S])*\[\/quote\]/g;

export function stripQuoteTokens(text: string): string {
    let out = text;
    let previous: string;
    do {
        previous = out;
        out = out.replace(INNERMOST_QUOTE, "");
    } while (out !== previous);
    return out.replace(/\n{3,}/g, "\n\n").trim();
}

export function buildQuoteToken(username: string, commentId: string, body: string): string {
    return `[quote user=${username} comment=${commentId}]\n${stripQuoteTokens(body)}\n[/quote]`;
}

function indentOf(state: StateBlock, line: number): number {
    return (state.sCount[line] ?? 0) - state.blkIndent;
}

function lineText(state: StateBlock, line: number): string {
    const start = (state.bMarks[line] ?? 0) + (state.tShift[line] ?? 0);
    return state.src.slice(start, state.eMarks[line] ?? state.src.length);
}

export function quoteBlocks(md: MarkdownIt, users: Map<UserId, User>) {
    const usersByName = new Map(
        Array.from(users.values()).map((u) => [u.userName.toLowerCase(), u])
    );

    function rule(state: StateBlock, startLine: number, endLine: number, silent: boolean): boolean {
        if (indentOf(state, startLine) >= 4) {
            return false;
        }

        const open = OPEN.exec(lineText(state, startLine));
        if (!open) {
            return false;
        }
        if (silent) {
            return true;
        }

        const [, user, comment] = open;

        let depth = 1;
        let nextLine = startLine;
        while (nextLine < endLine - 1) {
            nextLine++;
            if (indentOf(state, nextLine) >= 4) {
                continue;
            }
            const text = lineText(state, nextLine);
            if (OPEN.test(text)) {
                depth++;
            } else if (CLOSE.test(text)) {
                depth--;
                if (depth === 0) {
                    break;
                }
            }
        }

        const oldParentType = state.parentType;
        const oldLineMax = state.lineMax;
        state.parentType = "blockquote";
        state.lineMax = nextLine;

        let token = state.push("quote_block_open", "blockquote", 1);
        token.block = true;
        token.markup = "[quote]";
        token.map = [startLine, nextLine];
        token.meta = { user, comment };

        state.md.block.tokenize(state, startLine + 1, nextLine);

        token = state.push("quote_block_close", "blockquote", -1);
        token.block = true;
        token.markup = "[/quote]";

        state.parentType = oldParentType;
        state.lineMax = oldLineMax;
        state.line = nextLine + 1;

        return true;
    }

    const options = { alt: ["paragraph", "reference", "blockquote", "list"] };
    try {
        md.block.ruler.at("quoteBlocks", rule, options);
    } catch {
        md.block.ruler.before("blockquote", "quoteBlocks", rule, options);
    }

    md.renderer.rules.quote_block_open = (tokens, idx) => {
        const meta = tokens[idx]?.meta as { user: string; comment: string } | undefined;
        if (!meta) {
            return '<blockquote class="ticket-quote">';
        }
        const user = usersByName.get(meta.user.toLowerCase());
        const name = user?.displayName || user?.userName || meta.user;
        const escapedName = md.utils.escapeHtml(name);
        const escapedId = md.utils.escapeHtml(meta.comment);
        return (
            `<blockquote class="ticket-quote">` +
            `<div class="ticket-quote__attr">` +
            `<a class="ticket-quote__source" href="#comment-${escapedId}" ` +
            `data-comment-id="${escapedId}">` +
            `<span class="ticket-quote__author">${escapedName}</span>` +
            `<span class="ticket-quote__said">said:</span>` +
            `</a></div>`
        );
    };
    md.renderer.rules.quote_block_close = () => `</blockquote>`;
}
